import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import CloseIcon from '@assets/icons/close.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { BlurView } from 'expo-blur';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_SPRING_CONFIG_DISMISS,
  DEFAULT_SPRING_CONFIG_INTRO,
} from '@experiences/blocs/utils/animations';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { shadow } from '@/styles';
import { SpringConfig } from 'react-native-reanimated/lib/typescript/animation/springUtils';

const DEFAULT_PANEL_WIDTH = 475;
const DRAWER_SPACING = 8;

/** View ref on web is the host node (DOM element), which has focus(). */
type DrawerContentRef = View & { focus?: () => void };

type DrawerDirection = 'left' | 'right';
type BackdropType = 'blurDim' | 'none';

export interface SideDrawerRef {
  /** Starts the dismiss animation immediately on the UI thread. The parent must still set isOpen to false; only that unmounts the drawer. */
  dismiss: () => void;
}

interface SideDrawerProps {
  animationConfig?: SpringConfig;
  backdrop?: BackdropType;
  children: React.ReactNode;
  direction?: DrawerDirection;
  dismissAnimationConfig?: SpringConfig;
  dismissOnBackdropPress?: boolean;
  headerElement?: React.ReactNode;
  height?: number;
  isOpen: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
  width?: number;
}

export const SideDrawer = React.forwardRef<SideDrawerRef, SideDrawerProps>(
  (
    {
      animationConfig = DEFAULT_SPRING_CONFIG_INTRO,
      backdrop = 'blurDim',
      children,
      direction = 'right',
      dismissAnimationConfig = DEFAULT_SPRING_CONFIG_DISMISS,
      dismissOnBackdropPress = true,
      headerElement,
      height,
      isOpen,
      onClose,
      showCloseButton = true,
      width = DEFAULT_PANEL_WIDTH,
    },
    ref,
  ) => {
    const [shouldRender, setShouldRender] = useState(false);
    const [styles] = useStyles();
    const isOpenRef = useRef(isOpen);
    const contentRef = useRef<DrawerContentRef | null>(null);

    // keep ref in sync with latest isOpen value
    isOpenRef.current = isOpen;

    /**
     * WORKAROUND: React Native Web Modal **does not** auto-focus content when opened.
     * Focus stays on the previously focused element (usually the main page), so keyboard
     * events don't reach the drawer. This is a known issue with RN Modal due to a race
     * condition between the modal animation and focus management
     * ref: https://github.com/necolas/react-native-web/issues/1020
     *
     * We explicitly focus the drawer container after it renders. Defer by two frames
     * so the modal has painted and focus doesn't race with the open animation.
     */
    useEffect(() => {
      if (Platform.OS !== 'web' || !isOpen || !shouldRender) {
        return;
      }
      let cancelled = false;
      const id1 = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }
          contentRef.current?.focus?.();
        });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id1);
      };
    }, [isOpen, shouldRender]);

    // determine the slide distance and transform axis based on direction
    const isHorizontal = direction === 'left' || direction === 'right';
    const size = isHorizontal ? width : height || width;
    // add extra offset to guarantee drawer is completely offscreen including margins / shadows
    const slideDistance = size + 50;

    // determine initial offset based on direction
    const initialOffset =
      direction === 'right' ? slideDistance : -slideDistance;

    const translateValue = useSharedValue(initialOffset);
    const backdropOpacity = useSharedValue(0);

    /** Runs on the JS thread. A ref read inside the worklet is a stale copy on native. */
    const finishDismiss = useCallback((finished: boolean) => {
      if (finished && !isOpenRef.current) {
        setShouldRender(false);
      }
    }, []);

    /** Do not skip this when dismiss() already ran. If that spring finished before isOpen became false, nothing else unmounts the drawer, and the invisible modal blocks every tap. */
    const startDismiss = useCallback(() => {
      const closeConfig = dismissAnimationConfig || animationConfig;
      backdropOpacity.value = withSpring(0, closeConfig);
      translateValue.value = withSpring(
        initialOffset,
        closeConfig,
        (finished) => {
          runOnJS(finishDismiss)(finished === true);
        },
      );
    }, [
      animationConfig,
      backdropOpacity,
      dismissAnimationConfig,
      finishDismiss,
      initialOffset,
      translateValue,
    ]);

    // PERF: Imperative dismiss starts the spring immediately on the UI thread,
    // bypassing the useEffect delay that waits for a full React re-render.
    useImperativeHandle(ref, () => ({ dismiss: startDismiss }), [startDismiss]);

    useEffect(() => {
      if (isOpen) {
        setShouldRender(true);
        backdropOpacity.value = 1;
        translateValue.value = withSpring(0, animationConfig);
      } else {
        startDismiss();
      }
      // sharedValues (translateValue, backdropOpacity) are stable refs from useSharedValue
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, animationConfig, startDismiss]);

    const drawerAnimatedStyle = useAnimatedStyle(() => {
      const transform = isHorizontal
        ? [{ translateX: translateValue.value }]
        : [{ translateY: translateValue.value }];

      return { transform };
    });

    const backdropAnimatedStyle = useAnimatedStyle(() => ({
      opacity: backdropOpacity.value,
    }));

    // compute drawer positioning based on direction
    const getDrawerPositionStyle = (): ViewStyle => {
      const baseStyle: ViewStyle = {
        position: 'absolute',
      };

      switch (direction) {
        case 'right':
          return {
            ...baseStyle,
            bottom: DRAWER_SPACING,
            right: DRAWER_SPACING,
            top: DRAWER_SPACING,
            width: size,
          };
        case 'left':
          return {
            ...baseStyle,
            bottom: DRAWER_SPACING,
            left: DRAWER_SPACING,
            top: DRAWER_SPACING,
            width: size,
          };
      }
    };

    const drawerStyle: ViewStyle = {
      ...styles.drawer,
      ...getDrawerPositionStyle(),
    };

    const pointerEvents = isOpen ? 'auto' : 'none';

    if (!shouldRender) {
      return null;
    }

    const renderBackdrop = () => {
      if (backdrop === 'none') {
        return null;
      }

      const handleBackdropPress = dismissOnBackdropPress ? onClose : undefined;

      return (
        <Animated.View
          pointerEvents={isOpen ? 'auto' : 'none'}
          style={[styles.blurView, backdropAnimatedStyle]}
        >
          <BlurView
            experimentalBlurMethod="dimezisBlurView"
            intensity={20}
            style={styles.blurView}
            tint="light"
          >
            <View style={styles.dimOverlay}>
              <Pressable
                onPress={handleBackdropPress}
                style={styles.backdropPress}
              />
            </View>
          </BlurView>
        </Animated.View>
      );
    };

    return (
      <Modal onRequestClose={onClose} transparent visible>
        <View pointerEvents={pointerEvents} style={styles.container}>
          {renderBackdrop()}
          <Animated.View
            pointerEvents={pointerEvents}
            ref={contentRef}
            style={[drawerStyle, drawerAnimatedStyle]}
            tabIndex={-1}
          >
            <View style={styles.header}>
              {showCloseButton ? (
                <Pressable
                  accessibilityLabel={i18n.t('close')}
                  onPress={onClose}
                  style={styles.closeButton}
                >
                  <CloseIcon
                    fill={styles.closeIcon.color}
                    height={24}
                    width={24}
                  />
                </Pressable>
              ) : null}
              {headerElement}
            </View>
            <View style={styles.drawerBody}>{children}</View>
          </Animated.View>
        </View>
      </Modal>
    );
  },
);

const DIM_BACKDROP_COLOR = 'rgba(0, 0, 0, 0.1)';

const useStyles = createStyles((theme) => ({
  backdropPress: {
    flex: 1,
  },
  blurView: {
    ...StyleSheet.absoluteFillObject,
    flex: 1,
  },
  closeButton: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    color: theme.sk.color.icon.primary,
  },
  container: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
    zIndex: 100,
  },
  dimOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: DIM_BACKDROP_COLOR,
  },
  drawer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'column',
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingVertical: theme.sk.spacing.spaceMedium,
    ...shadow({
      opacity: 0.08,
      radius: 24,
      x: 0,
      y: 12,
    }),
  },
  drawerBody: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: theme.sk.spacing.spaceMedium,
  },
}));
